/**
 * Afuna Aromatics — cart-aware ritual recommendations. R2 Phase 5.
 * Scoped to product.sisterhood-ritual (conditional enqueue in layout).
 *
 * Complete Your Ritual: the opposite-format card of THIS blend is
 * suppressed only when that exact product is already in the cart — then a
 * compact "in cart" line replaces the large card and Meet Your Next Ritual
 * gets the visual emphasis. It is never suppressed just because another
 * blend of the same format is in the cart.
 *
 * Meet Your Next Ritual: never recommends a blend already represented in
 * the cart (either format present == represented). Walks the exact R2
 * candidate order for the current blend, picks the first non-represented
 * blend, and rewrites the card (duo image, family link, both format
 * routes). If every other blend is represented, shows a compact
 * completion state with a Collection route.
 *
 * Recalculates after confirmed add / remove / quantity cart events.
 * Enhancement only — the server-rendered curated cards are the fallback.
 */
(function () {
  'use strict';

  var CART_EVENT = 'shopify:cart:lines-update';

  // R2 "Meet Your Next Ritual" candidate order, per current blend.
  var CANDIDATES = {
    'crown-of-clarity': ['sacred-balance', 'release', 'rise-and-reign', 'sovereign', 'evening-reign'],
    'sacred-balance': ['release', 'crown-of-clarity', 'rise-and-reign', 'sovereign', 'evening-reign'],
    'release': ['crown-of-clarity', 'sacred-balance', 'rise-and-reign', 'sovereign', 'evening-reign'],
    'rise-and-reign': ['sovereign', 'evening-reign', 'crown-of-clarity', 'sacred-balance', 'release'],
    'sovereign': ['evening-reign', 'rise-and-reign', 'crown-of-clarity', 'sacred-balance', 'release'],
    'evening-reign': ['rise-and-reign', 'sovereign', 'crown-of-clarity', 'sacred-balance', 'release'],
  };
  var NAMES = {
    'crown-of-clarity': 'Crown of Clarity',
    'sacred-balance': 'Sacred Balance',
    'release': 'Release',
    'rise-and-reign': 'Rise & Reign',
    'sovereign': 'Sovereign',
    'evening-reign': 'Evening Reign',
  };
  var DUO = {
    'crown-of-clarity': 'AA_CROWN_OF_CLARITY_DUO_PLACEHOLDER_R0.png',
    'release': 'AA_RELEASE_DUO_CANDIDATE_R0.png',
    'sacred-balance': 'AA_SACRED_BALANCE_DUO_CANDIDATE_R0.png',
    'rise-and-reign': 'AA_RISE_AND_REIGN_DUO_CANDIDATE_R0.png',
    'sovereign': 'AA_SOVEREIGN_DUO_CANDIDATE_R0.png',
    'evening-reign': 'AA_EVENING_REIGN_DUO_CANDIDATE_R0.png',
  };

  var tplEl = document.querySelector('[data-template]');
  var tpl = (tplEl && tplEl.getAttribute('data-template')) || '';
  if (tpl.indexOf('product.sisterhood-ritual') !== 0) return;

  var m = location.pathname.match(/\/products\/([^/?#]+)/);
  if (!m) return;
  var handle = m[1];
  var currentBlend = handle.replace('-body-oil', '').replace('-mood-mist', '');
  var currentIsOil = handle.indexOf('-body-oil') > -1;
  var siblingHandle = currentIsOil
    ? currentBlend + '-mood-mist'
    : currentBlend + '-body-oil';

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }

  function assetSwap(existingSrc, filename) {
    if (!existingSrc) return '';
    return existingSrc.replace(/[^/]+\.(png|jpg|jpeg|webp)(\?[^#]*)?$/i, filename);
  }

  function getCartHandles() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        var set = {};
        if (cart && Array.isArray(cart.items)) {
          cart.items.forEach(function (it) {
            if (it && it.handle) set[it.handle] = true;
          });
        }
        return set;
      })
      .catch(function () { return {}; });
  }

  function blendRepresented(slug, cartHandles) {
    return !!cartHandles[slug + '-body-oil'] || !!cartHandles[slug + '-mood-mist'];
  }

  function applyComplete(cartHandles) {
    var section = document.querySelector('.aa-ritual-recommendations--complete');
    if (!section) return;
    var grid = section.querySelector('.aa-ritual-recommendations__grid');
    var siblingInCart = !!cartHandles[siblingHandle];

    var note = section.querySelector('[data-aa-complete-note]');
    if (siblingInCart) {
      if (grid) grid.hidden = true;
      if (!note) {
        note = document.createElement('p');
        note.setAttribute('data-aa-complete-note', '');
        note.className = 'aa-ritual-recommendations__in-cart';
        note.setAttribute('role', 'status');
        (section.querySelector('.aa-ritual-recommendations__inner') || section).appendChild(note);
      }
      note.textContent = 'The other half of this ritual is already in your cart.';
      document.body.classList.add('aa-recos--emphasize-next');
    } else {
      if (grid) grid.hidden = false;
      if (note) note.remove();
      document.body.classList.remove('aa-recos--emphasize-next');
    }
  }

  function applyNext(cartHandles) {
    var section = document.querySelector('.aa-ritual-recommendations--next');
    if (!section) return;
    var card = section.querySelector('.aa-next-ritual-card');
    if (!card) return;

    var order = (CANDIDATES[currentBlend] || []).slice();
    var pick = null;
    for (var i = 0; i < order.length; i++) {
      if (order[i] === currentBlend) continue;
      if (!blendRepresented(order[i], cartHandles)) { pick = order[i]; break; }
    }

    var inner = section.querySelector('.aa-ritual-recommendations__inner') || section;
    var done = inner.querySelector('[data-aa-next-complete]');

    if (!pick) {
      // Every other blend represented -> compact completion state + Collection route.
      card.hidden = true;
      if (!done) {
        done = document.createElement('div');
        done.setAttribute('data-aa-next-complete', '');
        done.className = 'aa-next-ritual-card aa-next-ritual-card--complete';
        done.innerHTML =
          '<p>You have a blend from every family in your cart.</p>' +
          '<a class="aa-recommendation-cta" href="/pages/the-collection">Explore the full Collection</a>';
        inner.appendChild(done);
      }
      return;
    }

    if (done) done.remove();
    card.hidden = false;

    var name = NAMES[pick];
    var familyUrl = '/pages/' + pick;

    var titleLink = card.querySelector('.aa-next-ritual-card__title-link');
    var mediaLink = card.querySelector('.aa-next-ritual-card__media');
    var img = card.querySelector('.aa-next-ritual-card__img');
    var ctas = card.querySelectorAll('.aa-next-ritual-card__format-cta');

    if (titleLink) { titleLink.textContent = name; titleLink.setAttribute('href', familyUrl); }
    if (mediaLink) mediaLink.setAttribute('href', familyUrl);
    if (img) {
      img.setAttribute('src', assetSwap(img.getAttribute('src'), DUO[pick]));
      img.setAttribute('alt', name + ' Body Oil and Mood Mist');
    }
    // Keep BOTH format routes (spray first, then body oil).
    if (ctas[0]) ctas[0].setAttribute('href', '/products/' + pick + '-mood-mist');
    if (ctas[1]) ctas[1].setAttribute('href', '/products/' + pick + '-body-oil');
  }

  function recalc() {
    getCartHandles().then(function (cartHandles) {
      try { applyComplete(cartHandles); } catch (e) { console.warn('[aa-recos] complete', e); }
      try { applyNext(cartHandles); } catch (e) { console.warn('[aa-recos] next', e); }
    });
  }

  ready(function () {
    recalc();
    document.addEventListener(CART_EVENT, function (event) {
      var run = function () { recalc(); };
      if (event && event.promise && typeof event.promise.finally === 'function') event.promise.finally(run);
      else setTimeout(run, 60);
    });
  });
})();
